import * as R from "ramda";
import { useState, useEffect, useRef, useMemo, useCallback } from "react";
import { useHistory } from "react-router-dom";
import { parseQuery, stringifyQuery } from "../utils/query";

export const STRING_DESCRIPTOR = {
  encode(value) {
    return value;
  },
  decode(value) {
    return value;
  },
};

const decodeQuery = (values, descriptors) => {
  return descriptors.reduce((acc, descriptor) => {
    const value = values[descriptor.name];
    const decoder = descriptor.decode || STRING_DESCRIPTOR.decode;

    acc[descriptor.name] = decoder(value);

    return acc;
  }, {});
};

const encodeQuery = (values, descriptors) => {
  return descriptors.reduce((acc, descriptor) => {
    const value = values[descriptor.name];
    const encoder = descriptor.encode || STRING_DESCRIPTOR.encode;

    if (R.isNil(value)) return acc;

    acc[descriptor.name] = encoder(value);

    return acc;
  }, {});
};

/**
 *
 * @param {Array<{ name: String, descriptor: { encode: Function, decode: Function} }>} descriptors should be memoized array
 * @returns {[Object, Function]}
 */
const useQueryState = (descriptors) => {
  const history = useHistory();
  const initialState = useMemo(
    () => decodeQuery(parseQuery(history.location.search), descriptors),
    [history, descriptors]
  );
  const prevState = useRef(initialState);

  const [state, setState] = useState(initialState);

  useEffect(() => {
    const propertiesNames = R.pluck("name", descriptors);

    return history.listen((location) => {
      const stateAfter = R.pick(propertiesNames, parseQuery(location.search));
      const stateBefore = encodeQuery(prevState.current, descriptors);

      if (!R.equals(stateBefore, stateAfter)) {
        const decodedState = decodeQuery(stateAfter, descriptors);
        setState(decodedState); // force re-render
        prevState.current = decodedState;
      }
    });
  }, [history, descriptors]);

  const updater = useCallback(
    (values, { type = "replace", extend = true } = {}) => {
      let newState = null;

      if (extend) {
        newState = {
          ...parseQuery(history.location.search),
          ...encodeQuery(values, descriptors),
        };
      } else {
        newState = encodeQuery(values, descriptors);
      }

      if (type === "replace") {
        history.replace({
          search: stringifyQuery(newState),
        });
      } else {
        history.push({
          search: stringifyQuery(newState),
        });
      }
    },
    [descriptors, history]
  );

  return [state, updater];
};

export default useQueryState;
